import React, { useState } from 'react';
import { Chip, ProviderBadge } from './Chip.jsx';
import { IconX, IconPin, IconArrow } from './Icons.jsx';
import { CopyButton } from './CopyButton.jsx';
import { JsonTree, JsonRaw } from './JsonViewer.jsx';
import { providerOf } from './providers.js';
import { relTime } from './util.js';

export function DetailPanel({ event, baseUrl, onClose, onTogglePin, payloadView, setPayloadView, readOnly }) {
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [contentOpen, setContentOpen] = useState(true);
  const [headersOpen, setHeadersOpen] = useState(false);
  const url = `${baseUrl}/u/${event.endpointId}`;
  const provider = providerOf(event.headers?.['x-yuno-provider']);
  const ip = event.ip || event.headers?.['x-forwarded-for'];
  const ok = event.responseCode < 300;

  return (
    <aside className="detail-pane-container" style={{
      flex: 1, minWidth: 0,
      background: '#fff', borderLeft: '1px solid var(--border-default)',
      display: 'flex', flexDirection: 'column',
      height: '100%', overflow: 'auto',
    }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
        <ProviderBadge provider={provider} size={26} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)', fontFamily: 'var(--font-mono)' }}>
            {event.method} {event.path || '/'}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--fg-3)' }}>{relTime(event.receivedAt)}</div>
        </div>
        {onTogglePin && !readOnly && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={onTogglePin}
            title={event.pinned ? 'Pinned. Click to unpin.' : 'Pin to keep beyond retention'}
            style={{
              gap: 6,
              color: event.pinned ? '#4c1d95' : 'var(--fg-2)',
              background: event.pinned ? 'var(--yuno-purple-50)' : 'transparent',
              border: '1px solid', borderColor: event.pinned ? '#ddd6fe' : 'transparent',
            }}>
            <IconPin size={13} filled={event.pinned} />
            <span>{event.pinned ? 'Pinned' : 'Pin'}</span>
          </button>
        )}
        <button className="btn btn-ghost icon-only" onClick={onClose}><IconX size={14}/></button>
      </div>

      <Section title="Request Details & Headers" open={detailsOpen} setOpen={setDetailsOpen}>
        <div className="detail-grid" style={{ display: 'grid', gap: 24, padding: '14px 20px' }}>
          <div style={{ minWidth: 0 }}>
            <MetaRow label="">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
                <Chip tone="brand" size="sm" style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{event.method}</Chip>
                <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-2)', wordBreak: 'break-word', overflowWrap: 'anywhere', background: 'transparent', padding: 0, minWidth: 0 }}>{url}</code>
              </div>
            </MetaRow>
            {ip && <MetaRow label="IP"><code style={{ fontFamily: 'var(--font-mono)', fontSize: 12, background: 'transparent', padding: 0 }}>{ip}</code></MetaRow>}
            <MetaRow label="Provider">
              <ProviderBadge provider={provider} size={16} />
              <span style={{ marginLeft: 8 }}>{provider.name}</span>
            </MetaRow>
            <MetaRow label="Date">{new Date(event.receivedAt).toLocaleString()} <span style={{ color: 'var(--fg-3)' }}>({relTime(event.receivedAt)})</span></MetaRow>
            <MetaRow label="Size">{event.size} bytes</MetaRow>
            <MetaRow label="Time"><span style={{ fontFamily: 'var(--font-mono)' }}>{(event.latencyMs / 1000).toFixed(3)} sec</span></MetaRow>
            <MetaRow label="Response">
              <Chip tone={ok ? 'success' : 'danger'} size="sm" style={{ fontFamily: 'var(--font-mono)' }}>
                {event.responseCode} {ok ? 'OK' : 'ERR'}
              </Chip>
            </MetaRow>
            <MetaRow label="ID"><code style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, background: 'transparent', padding: 0, wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{event.id}</code></MetaRow>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Headers</div>
            {Object.entries(event.headers || {}).map(([k, v]) => (
              <div key={k} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 11.5, alignItems: 'baseline' }}>
                <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg-3)', background: 'transparent', padding: 0, wordBreak: 'break-all' }}>{k}</code>
                <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg-1)', background: 'transparent', padding: 0, wordBreak: 'break-all' }}>{Array.isArray(v) ? v.join(', ') : String(v)}</code>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section title="Request Content" open={contentOpen} setOpen={setContentOpen}
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={(e) => e.stopPropagation()}>
            <ToggleGroup value={payloadView} onChange={setPayloadView} options={[['tree', 'Tree'], ['raw', 'Raw'], ['dual', 'Dual']]}/>
            <CopyButton text={event.payload != null ? JSON.stringify(event.payload, null, 2) : (event.rawBody || '')} label="Copy" compact />
          </div>
        }>
        <div style={{ padding: '14px 20px' }}>
          <PayloadBody payload={event.payload} raw={event.rawBody} mode={payloadView} />
        </div>
      </Section>
    </aside>
  );
}

function PayloadBody({ payload, raw, mode }) {
  if (mode === 'dual') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: '100%' }}>
        <div style={{ padding: 16, borderRight: '1px solid var(--border-subtle)', overflow: 'auto' }}>
          <div style={{ fontSize: 10.5, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 500, marginBottom: 10 }}>Tree</div>
          <JsonTree value={payload} />
        </div>
        <div style={{ padding: 16, overflow: 'auto', background: 'var(--neutral-25)' }}>
          <div style={{ fontSize: 10.5, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 500, marginBottom: 10 }}>Raw</div>
          <JsonRaw value={payload} raw={raw} />
        </div>
      </div>
    );
  }
  if (mode === 'raw') return <JsonRaw value={payload} raw={raw} />;
  return <JsonTree value={payload} />;
}

function ToggleGroup({ value, onChange, options }) {
  return (
    <div style={{ display: 'inline-flex', background: 'var(--neutral-100)', borderRadius: 7, padding: 2, gap: 0 }}>
      {options.map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)} style={{
          padding: '4px 10px', fontSize: 12, fontWeight: 500,
          background: value === v ? '#fff' : 'transparent',
          color: value === v ? 'var(--fg-1)' : 'var(--fg-3)',
          border: 0, borderRadius: 5, cursor: 'pointer',
          boxShadow: value === v ? '0 1px 2px rgba(14,23,42,0.08)' : 'none',
        }}>{l}</button>
      ))}
    </div>
  );
}

function Section({ title, open, setOpen, right, children }) {
  return (
    <div style={{ borderBottom: '1px solid var(--border-default)' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px', cursor: 'pointer', userSelect: 'none', background: 'var(--neutral-25)' }}>
        <span style={{ color: 'var(--fg-3)', fontSize: 10, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 150ms' }}>▸</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>{title}</span>
        <div style={{ flex: 1 }} />
        {right}
      </div>
      {open && children}
    </div>
  );
}

function MetaRow({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 12.5, alignItems: 'baseline' }}>
      <span style={{ color: 'var(--fg-3)', fontWeight: 500 }}>{label}</span>
      <span style={{ color: 'var(--fg-1)', minWidth: 0 }}>{children}</span>
    </div>
  );
}

export function EmptyDetail() {
  return (
    <aside style={{
      flex: 1, minWidth: 0, background: '#fff',
      borderLeft: '1px solid var(--border-default)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', color: 'var(--fg-3)',
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: 14,
        background: 'var(--neutral-25)', border: '1px solid var(--border-default)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
      }}>
        <IconArrow size={20} stroke="var(--fg-4)" style={{ transform: 'rotate(180deg)' }}/>
      </div>
      <div style={{ fontSize: 14, color: 'var(--fg-2)', fontWeight: 500 }}>Select a request to inspect it</div>
      <div style={{ fontSize: 12, color: 'var(--fg-4)', marginTop: 4 }}>Headers, payload, and signature will appear here.</div>
    </aside>
  );
}
